import {
  DataFilterField, DataFilterKind, DataFilterOption,
} from '../../../shared/ui/data-filter/data-filter.component';
import { DynamicColumn, DynamicFieldType } from './dynamic-framework.models';

/**
 * What goes in front of a workflow field's key to make its column id - on the
 * table, on the filter bar, and in the rule the server reads
 * (DynamicWorkflowFilterFields.FieldPrefix). A contract test holds the two.
 */
export const SMART_WORKFLOW_FIELD_PREFIX = 'field__';

export function workflowFieldColumnId(fieldKey: string): string {
  return `${SMART_WORKFLOW_FIELD_PREFIX}${fieldKey}`;
}

/**
 * The filter kind each field data type is asked as, which decides the
 * comparisons its editor offers. Mirrors the server's
 * DynamicWorkflowFilterFields.KindsByDataType; a contract test holds the two
 * together. A yes/no field is a choice between two values, not a kind of its own.
 */
export const SMART_WORKFLOW_KIND_BY_DATA_TYPE: Readonly<Record<DynamicFieldType, DataFilterKind>> = {
  shortText: 'text',
  number: 'number',
  date: 'date',
  choice: 'choice',
  boolean: 'choice',
};

/** The server compares a yes/no value as the text `true` or `false`. */
const YES_NO: readonly DataFilterOption[] = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

/**
 * Every column of a workflow's records table that the Filters panel can narrow
 * by, in the order the table shows them: the record id, one per active
 * workflow field, and when the record last moved.
 *
 * The fields are the tenant's own, so unlike Customers or Roll-off this list is
 * built from the loaded workflow rather than written out. The server builds its
 * whitelist from the same workflow and refuses a column it does not have.
 *
 * Node is absent DELIBERATELY: it keeps its pinned control and its own
 * `nodeKey` parameter. Unpinned, all of them, so the bar stays as short as it
 * was until somebody adds a filter.
 */
export function smartWorkflowRuleFilterFields(
  fields: readonly DynamicColumn[],
): DataFilterField[] {
  const none: readonly DataFilterOption[] = [];
  return [
    { id: 'id', label: 'ID', kind: 'number', pinned: false, options: none },
    ...fields.map(field => workflowFieldFilter(field)),
    { id: 'updatedAt', label: 'Updated', kind: 'date', pinned: false, options: none },
  ];
}

function workflowFieldFilter(field: DynamicColumn): DataFilterField {
  return {
    id: workflowFieldColumnId(field.key),
    label: field.label,
    kind: SMART_WORKFLOW_KIND_BY_DATA_TYPE[field.dataType],
    pinned: false,
    options: fieldOptions(field),
  };
}

/**
 * A choice is offered by option KEY, which is what the server compares and what
 * survives a tenant relabelling the option; the label is only what the dropdown
 * shows. Retired options are not offered.
 */
function fieldOptions(field: DynamicColumn): readonly DataFilterOption[] {
  if (field.dataType === 'boolean') return YES_NO;
  if (field.dataType !== 'choice') return [];
  return field.options
    .filter(option => option.isActive)
    .map(option => ({ value: option.key, label: option.label }));
}
